import React, { useEffect, useState } from 'react';

import {
  getAiScoringAssistSettings,
  updateAiScoringAssistSettings,
} from '../../services/districtsService';
import { Button } from '../common/Button';
import { Card, CardContent, CardHeader, CardTitle } from '../common/Card';

import type { AiScoringAssistSettings as AiScoringAssistSettingsDto } from '../../services/districtsService';

interface AiScoringAssistSettingsProps {
  districtId: number;
}

export const AiScoringAssistSettings: React.FC<AiScoringAssistSettingsProps> = ({
  districtId,
}) => {
  const [settings, setSettings] = useState<AiScoringAssistSettingsDto | null>(
    null
  );
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    let active = true;
    setLoading(true);
    void getAiScoringAssistSettings(districtId)
      .then(response => {
        if (active) setSettings(response);
      })
      .catch(err => {
        if (active) {
          setError(
            err instanceof Error ? err.message : 'Failed to load AI assist settings'
          );
        }
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [districtId]);

  const handleToggle = async () => {
    if (!settings) return;
    try {
      setSaving(true);
      setError(null);
      const response = await updateAiScoringAssistSettings(districtId, {
        enabled: !settings.enabled,
      });
      setSettings(response);
    } catch (err) {
      setError(
        err instanceof Error ? err.message : 'Failed to update AI assist settings'
      );
    } finally {
      setSaving(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <CardTitle>AI-Assisted Evaluation</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-gray-600">
          Control whether evaluators can transcribe lesson recordings and generate
          AI draft rubric scores for this district. Human evaluators always submit
          final scores.
        </p>
        {loading ? (
          <p className="text-sm text-gray-500">Loading…</p>
        ) : error ? (
          <p className="text-sm text-red-600">{error}</p>
        ) : settings ? (
          <div className="flex flex-wrap items-center gap-3">
            <p className="text-sm font-medium text-gray-900">
              Status: {settings.enabled ? 'Enabled' : 'Disabled'}
            </p>
            <Button
              variant={settings.enabled ? 'secondary' : 'primary'}
              onClick={() => void handleToggle()}
              disabled={saving}
            >
              {settings.enabled ? 'Disable AI assist' : 'Enable AI assist'}
            </Button>
          </div>
        ) : null}
      </CardContent>
    </Card>
  );
};
